
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type AnalyticsInsert = Database['public']['Tables']['prayer_engagement_analytics']['Insert'];

export type EngagementType = 
  | 'choice_dialog_opened'
  | 'i_need_prayer_selected'
  | 'i_want_to_pray_selected'
  | 'ai_assistant_opened'
  | 'manual_prayer_completed'
  | 'prayer_committed';

export interface AnalyticsEvent {
  prayerId?: string;
  engagementType: EngagementType;
  sourceContext?: string;
  metadata?: Record<string, any>;
  sessionId?: string;
}

// Rate limiting and batching for analytics
const eventQueue: AnalyticsEvent[] = [];
const eventCache = new Set<string>();
let batchTimeout: NodeJS.Timeout | null = null;
const BATCH_SIZE = 10;
const BATCH_DELAY = 2000; // 2 seconds
const RATE_LIMIT_WINDOW = 1000; // 1 second
let lastBatchTime = 0;

export const PrayerAnalyticsService = {
  /**
   * Track a prayer engagement event with rate limiting and batching
   */
  async trackEngagement(event: AnalyticsEvent) {
    // Create a unique key for deduplication
    const eventKey = `${event.prayerId}-${event.engagementType}-${event.sourceContext}`;
    
    // Skip if we've seen this exact event recently (deduplication)
    if (eventCache.has(eventKey)) {
      return;
    }
    
    // Add to cache with expiration
    eventCache.add(eventKey);
    setTimeout(() => eventCache.delete(eventKey), RATE_LIMIT_WINDOW);
    
    // Add to queue
    eventQueue.push(event);
    
    // Process batch if queue is full or schedule batch processing
    if (eventQueue.length >= BATCH_SIZE) {
      await this._processBatch();
    } else if (!batchTimeout) {
      batchTimeout = setTimeout(() => this._processBatch(), BATCH_DELAY);
    }
  },

  /**
   * Process batched events
   */
  async _processBatch() {
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      batchTimeout = null;
    }
    
    if (eventQueue.length === 0) return;
    
    // Rate limiting check
    const now = Date.now();
    if (now - lastBatchTime < RATE_LIMIT_WINDOW) {
      // Reschedule if we're hitting rate limits
      batchTimeout = setTimeout(() => this._processBatch(), RATE_LIMIT_WINDOW);
      return;
    }
    
    lastBatchTime = now;
    const events = eventQueue.splice(0, BATCH_SIZE);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const insertData: AnalyticsInsert[] = events.map(event => ({
        user_id: user?.id || null,
        prayer_id: event.prayerId || null,
        engagement_type: event.engagementType,
        source_context: event.sourceContext || null,
        metadata: event.metadata || {},
        session_id: event.sessionId || null
      }));

      const { error } = await supabase
        .from('prayer_engagement_analytics')
        .insert(insertData);

      if (error) {
        console.warn('Analytics batch failed:', error.message);
        // Don't throw - analytics failures shouldn't break the user experience
      }
    } catch (error) {
      console.warn('Analytics batch error:', error);
    }
    
    // Continue processing if there are more events
    if (eventQueue.length > 0) {
      batchTimeout = setTimeout(() => this._processBatch(), BATCH_DELAY);
    }
  },

  /**
   * Get engagement summary for a prayer
   */
  async getPrayerEngagementSummary(prayerId: string) {
    const { data, error } = await supabase
      .rpc('get_prayer_engagement_summary', { target_prayer_id: prayerId });

    if (error) throw error;
    return data?.[0] || {
      total_choice_actions: 0,
      i_need_prayer_count: 0,
      i_want_to_pray_count: 0,
      ai_assistant_usage: 0,
      manual_prayer_usage: 0
    };
  },

  /**
   * Get user engagement metrics
   */
  async getUserEngagementMetrics(userId: string, timeRange?: { start: Date; end: Date }) {
    let query = supabase
      .from('prayer_engagement_analytics')
      .select('*')
      .eq('user_id', userId);

    if (timeRange) {
      query = query
        .gte('created_at', timeRange.start.toISOString())
        .lte('created_at', timeRange.end.toISOString());
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
};
